import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// Stable empty-array reference: `?? []` returned a NEW array every render
// when the query had no data, so consumers using `tickets` as a useEffect
// dep (NotificationsPopOver) re-ran their effect every render -> setState ->
// re-render -> Maximum update depth. One shared constant fixes it for all
// four ticket panels + notifications.
const EMPTY_TICKETS = [];

// Debounce a serialised key. Object inputs are serialised so the
// effect dep stays stable across renders (otherwise a new object
// reference each render would reset the timer forever).
//
// Two-tier debounce (2026-06-09 perf fix):
//   • Initial mount (no prior value): 0 ms — fetch immediately so the
//     list appears as soon as the user enters the tickets area.
//   • Subsequent filter changes: `delay` ms — prevents hammering
//     /tickets on every search keystroke or rapid filter toggle.
// Without this split, the 1-sec blanket wait caused 4 panels to all
// start their timers simultaneously on entry → 3 APIs resolved first,
// 1 lagged by ~100ms → user saw "3 tickets then 1 more a second later".
const useDebouncedKey = (value, delay) => {
  const key = JSON.stringify(value);
  // initialised to the FIRST key so the very first render fires immediately
  const [debounced, setDebounced] = useState(key);
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      // First mount: apply immediately (no debounce), skip the timer.
      isFirstRender.current = false;
      setDebounced(key);
      return;
    }
    const t = setTimeout(() => setDebounced(key), delay);
    return () => clearTimeout(t);
  }, [key, delay]);
  return debounced;
};

const useTickets = ({
  isSearch,
  searchParam,
  tags,
  users,
  whatsapps,
  pageNumber,
  status,
  groups,
  date,
  updatedAt,
  showAll,
  queueIds,
  withUnreadMessages,
  all,
  sectionId,
  channel,
  // Opaque cache-buster: not sent to the API, only mixed into the
  // react-query cache key. Callers use this to force a fresh fetch
  // when something OUTSIDE the filter set affects what /tickets
  // returns — currently the sections list (a custom section being
  // deleted means previously-claimed tickets re-enter the system
  // tabs, but useTickets has no other way to know about it).
  cacheSalt,
}) => {
  // 300 ms debounce on filter changes (search typing, queue toggles).
  // Initial mount fires at 0 ms via useDebouncedKey's first-render path.
  // The old 1000 ms blanket caused all 4 panels to stall 1 second on
  // every entry to the tickets area — "3 visible then 1 more after 1s".
  const filters = {
    isSearch,
    searchParam,
    pageNumber,
    tags,
    users,
    whatsapps,
    status,
    groups,
    date,
    updatedAt,
    showAll,
    queueIds,
    withUnreadMessages,
    all,
    sectionId,
    channel,
  };
  const debouncedKey = useDebouncedKey(filters, 300);
  const debouncedFilters = useMemo(() => JSON.parse(debouncedKey), [debouncedKey]);

  // Tenant fingerprint stays inside the cache key so company A's
  // tickets never get served to a freshly-logged-in company B in the
  // same tab. handleLogout/handleLogin in useAuth already purge the
  // cache, but this is the belt-and-suspenders — defends against the
  // class of bugs where state leaks across an in-tab account switch
  // (Danylo 2026-05-15 cross-tenant notification dropdown).
  const tenantKey = typeof window !== "undefined"
    ? `${localStorage.getItem("companyId") || ""}:${localStorage.getItem("userId") || ""}`
    : "";

  const { data, isFetching, isLoading } = useQuery(
    ["tickets", tenantKey, debouncedKey, cacheSalt ?? ""],
    async () => {
      const { data } = await api.get("/tickets", { params: debouncedFilters });
      return data; // { tickets, hasMore }
    },
    {
      // Raviv's 2026-05-13 audit: keepPreviousData + a non-zero stale
      // window conflicted with the socket-driven reducer. Within the
      // stale window a socket-driven DELETE_TICKET would be reverted
      // when react-query re-served the cached page and LOAD_TICKETS
      // ran again — ghost tickets reappeared after every transfer /
      // status change. Same window also made LOAD_TICKETS push new
      // entries to the bottom, breaking the server's ORDER BY
      // updatedAt DESC.
      //
      // Branch 14 (fix/tickets-realtime-sync) tried to re-introduce a
      // 10s stale + refetchOnWindowFocus to catch up after the socket
      // drops. Kept the audit's settings instead because the same
      // branch ALSO wires queryClient.invalidateQueries(["tickets"])
      // on the socket "reconnect" event (see TicketsListCustom) — that
      // path achieves the catch-up without re-opening the ghost-ticket
      // window. With staleTime: 0 the invalidateQueries triggers an
      // immediate refetch, exactly the behaviour Raviv described.
      staleTime: 0,
      refetchOnWindowFocus: false,
      onError: err => toastError(err),
    },
  );

  // `isLoading` is true only on the FIRST fetch (no prior cache) —
  // perfect for the skeleton placeholder. `isFetching` flips true
  // on every background refetch too, which made the list flash a
  // skeleton each time the user clicked a ticket. Expose both so
  // the list view can pick the right one.
  return {
    tickets: data?.tickets ?? EMPTY_TICKETS,
    hasMore: data?.hasMore ?? false,
    loading: isFetching,
    initialLoading: isLoading,
  };
};

export default useTickets;
